/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useCallback, useState } from 'react';
import { useFetcher } from 'react-router';

import { resourceRoutes } from '@/route-paths';
import { PRODUCT_LISTING_VIEW_FIELD, type ProductListingView } from '@/lib/product/view-preference';

/**
 * Grid/list view state for a product listing (CRCB2B-87).
 *
 * Shaped after {@link useFiltersPanelState}, which is the established pattern in the same
 * route: the loader resolves the initial value on the server, the hook owns the interactive
 * copy, and the route hands the value to every consumer that needs it.
 *
 * ## The switch is optimistic; the fetcher only persists
 *
 * Local state flips immediately, so the presentation changes with no navigation — the AC asks for
 * the layout to update "immediately without page reload". The `useFetcher` submission exists purely
 * to write the cookie, and **nothing waits on it**.
 *
 * "No refetch" holds only because the action is listed in `ROOT_IRRELEVANT_MUTATIONS`
 * (`lib/revalidation/routes/root.ts`). React Router revalidates every route's loader after an
 * action by default, and a denylist miss there costs a second round trip per toggle even though
 * the category loader itself stays put. If a new loader ever needs to react to the view, that
 * entry is the thing to revisit.
 * Deliberately no pending state and no disabling: blocking the control on a round trip is the
 * behaviour the AC rules out. If the write fails the shopper keeps their chosen view for this
 * page and reverts on the next load, which is an acceptable outcome for a display preference.
 *
 * ## The loader value is a seed, not a subscription
 *
 * `initialView` is read once, on mount. It deliberately does NOT re-sync when the loader value
 * changes: navigating between listing pages reuses this route component, so local state
 * survives, and re-seeding would mean a failed cookie write silently snapping the shopper back
 * to a view they had moved away from. Their explicit choice wins for the session; the cookie is
 * best-effort persistence beyond it.
 *
 * An earlier version had a `useEffect` re-seeding on `initialView`. It could not work as
 * intended anyway — the dependency is the value, so a grid → (local list) → grid sequence never
 * re-fires — and the behaviour it advertised was not one we actually want. Removed rather than
 * repaired.
 */
export function useProductListingView(initialView: ProductListingView) {
    const fetcher = useFetcher();
    const [view, setView] = useState<ProductListingView>(initialView);

    const selectView = useCallback(
        (next: ProductListingView) => {
            if (next === view) return;

            setView(next);

            const formData = new FormData();
            formData.set(PRODUCT_LISTING_VIEW_FIELD, next);
            void fetcher.submit(formData, { method: 'POST', action: resourceRoutes.setProductListingView });
        },
        [fetcher, view]
    );

    return [view, selectView] as const;
}
